import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { contentSlug } from '../../../../content-slug';
import {
  CmsPage,
  CmsPageSection,
  PageTemplate,
  Region,
  SUBREGION_KIND,
  Suburb,
  SuburbBlock,
} from '../../../../models/content';
import { ModalService } from '../../../../services/modal.service';
import { COMPONENT_OPTIONS } from '../../content-sections-editor.component';
import { regionHubPath, shortId } from '../content/suburb-content';
import { previewTokens } from '../template/suburb-template';
import { apiErrorBody } from '../workspace/api-errors';
import { PageTabService } from '../workspace/page-tab.service';
import { PanelNavService } from '../workspace/panel-nav.service';
import { StagePreviewService } from '../workspace/stage-preview.service';
import { PageTabPreview } from './page-preview';
import {
  addBlock,
  BlockFacts,
  blockKindLabel,
  blocksForSave,
  insertedList,
  moveBlock,
  OutlineRow,
  outline,
  removeBlock,
  replaceBlock,
  withFieldFacts,
} from './page-structure';
import { hasUnsavedChanges, pageTabProblem, pageTabValues, PageTabValues } from './page-tab';

/** Publish or Un-publish chosen in the tab; the host saves first, then ships it. */
export type SuburbPublishAction = 'publish' | 'unpublish' | null;

/**
 * A suburb's Page tab (#253, #262; also a place's, a sub-region's, and a new one's): the page is the template of its
 * kind filled from this record, so the tab edits the record. Its page settings, its own content (the fields the
 * template's blocks read; an empty one hides its block), and its structure: the template's blocks in order, with the
 * blocks inserted on this page only between them. Mounted in the side panel by PageEditorService.editPage with the
 * same DynamicDialogConfig data as a popup; Save writes the tab's changes onto a freshly read record and closes with
 * the result. The rules are pure (page-tab.ts, page-structure.ts); the reads and writes are PageTabService.
 */
@Component({
  selector: 'app-content-suburb',
  templateUrl: './content-suburb.component.html',
  styleUrls: ['./content-suburb.component.scss'],
})
export class ContentSuburbComponent implements OnInit, OnDestroy {
  record!: Suburb;
  isNew = false;
  form!: FormGroup;
  regionOptions: Array<{ label: string; value: string }> = [];
  /** A suburb from the map: name and region come from there. */
  fromMap = false;
  /** On the website when the tab opened: a rename keeps the old address as a redirect. */
  live = false;
  subRegion = false;
  /** A sub-region's suburbs: while it has any, it stays in its region. */
  members = 0;

  template!: PageTemplate;
  blocks: SuburbBlock[] = [];
  rows: OutlineRow[] = [];
  /** The block open in the block editor: new (with where it goes) or one already on the page. */
  editing: { section: CmsPageSection; after: string | null } | null = null;
  faqOptions: Array<{ label: string; value: string }> = [];
  heroPreview: string | null = null;
  draftsWaiting = 0;
  /** "Preview page", and the block editor's preview (#263). */
  preview!: PageTabPreview;
  readonly blockPreview = (section: CmsPageSection) =>
    this.preview.block(section, this.editing?.after ?? null);
  /** A page designed before the template, still at this address until it is converted. */
  designedPage?: CmsPage;
  saving = false;

  private regions: Region[] = [];
  private serverFacts: BlockFacts = new Map();
  private dataLead: string | null = null;
  private absSlugs: ((state: string) => Set<string> | null) | null = null;
  private subs: Subscription[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public tab: PageTabService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private nav: PanelNavService,
    private stage: StagePreviewService
  ) {}

  ngOnInit(): void {
    const data = this.config.data;
    this.record = data.suburb;
    this.isNew = !!data.isNew;
    this.members = Number(data.members) || 0;
    this.absSlugs = typeof data.absSlugs === 'function' ? data.absSlugs : null;
    this.fromMap = !!this.record.salCode;
    this.live = !this.isNew && this.record.published === true;
    this.subRegion = this.record.kind === SUBREGION_KIND;
    this.regions = this.tab.regions();
    this.regionOptions = this.regions.map((r) => ({ label: r.name, value: r._meta.id }));
    this.template = this.tab.templateFor(this.record);
    this.blocks = insertedList(this.record.blocks);
    this.faqOptions = this.tab.faqOptions();
    this.designedPage = this.isNew ? undefined : this.tab.designedPage(this.record);
    this.preview = new PageTabPreview(this.stage, () => {
      const { record, template, blocks, fromMap, live } = this;
      return { record, values: this.values, template, blocks, fromMap, live };
    });
    this.buildForm(pageTabValues(this.record));
    this.subs.push(this.form.valueChanges.subscribe(() => this.refresh()));
    this.loadFacts();
    if (this.record.heroFileId) this.showHero(this.record.heroFileId);
    this.refresh();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.preview.dispose();
  }

  private buildForm(v: PageTabValues): void {
    const regionLocked = this.live || (this.subRegion && this.members > 0);
    this.form = this.fb.group({
      name: [v.name, this.fromMap ? [] : [Validators.required]],
      regionId: [
        { value: v.regionId, disabled: regionLocked },
        this.fromMap ? [] : [Validators.required],
      ],
      slug: [v.slug, [Validators.required]],
      inSentence: [v.inSentence],
      seoDescription: [v.seoDescription],
      noindex: [v.noindex],
      intro: [v.intro],
      body: [v.body],
      heroFileId: [v.heroFileId],
      faqIds: [v.faqIds],
    });
  }

  private get values(): PageTabValues {
    return this.form.getRawValue() as PageTabValues;
  }

  private loadFacts(): void {
    this.tab
      .facts(this.record, this.isNew)
      .pipe(take(1))
      .subscribe((d) => {
        if (!d) return;
        this.serverFacts = new Map(d.blocks.map((b) => [b.id, b]));
        this.dataLead = d.opening.dataLead;
        this.draftsWaiting = d.opening.draftsWaiting;
        this.refresh();
      });
  }

  /** The outline, from the template, the blocks and what the tab has now. */
  private refresh(): void {
    const v = this.values;
    const facts = withFieldFacts(this.template, this.serverFacts, {
      ...v,
      dataLead: this.dataLead,
    });
    const tokens = previewTokens(
      { ...this.record, name: v.name, inSentence: v.inSentence },
      this.region
    );
    this.rows = outline(this.template, this.blocks, tokens, facts, (b) =>
      blockKindLabel(b, COMPONENT_OPTIONS)
    );
    this.preview.changed();
  }

  get region(): Region | undefined {
    const id = this.fromMap ? this.record.regionId : this.values.regionId;
    return this.regions.find((r) => r._meta.id === id);
  }

  // ------------------------------------------------------------------ page settings

  get pathPrefix(): string {
    return this.region ? `${regionHubPath(this.region)}/funeral-services/` : '/…/funeral-services/';
  }

  /** A live page's address being changed: its old slug, for the redirect note. */
  get renamingFrom(): string {
    const typed = contentSlug(this.values.slug || '');
    return this.live && this.record.slug && typed && typed !== this.record.slug
      ? this.record.slug
      : '';
  }

  /** A sub-region whose slug is an ABS suburb's in the region's state: that suburb could never have its own page. */
  get slugClash(): boolean {
    const state = this.region?.state?.toUpperCase();
    const slug = contentSlug(this.values.slug || '');
    return this.subRegion && !!state && !!slug && !!this.absSlugs?.(state)?.has(slug);
  }

  get regionNote(): string {
    return this.subRegion && this.members > 0 && !this.live
      ? `It has ${this.members} suburb${this.members === 1 ? '' : 's'} in it, so it stays in this region.`
      : '';
  }

  /** As the name is typed (sub-region and place pages), keep the address in step until the page is live. */
  onNameInput(): void {
    if (!this.live) this.form.get('slug')!.setValue(contentSlug(this.values.name));
  }

  // ------------------------------------------------------------------ content

  uploadHero(): void {
    this.tab.uploadHero().subscribe((file) => {
      if (!file) return;
      this.form.get('heroFileId')!.setValue(file.id);
      this.heroPreview = file.link;
      if (!file.link) this.showHero(file.id);
    });
  }

  removeHero(): void {
    this.form.get('heroFileId')!.setValue(null);
    this.heroPreview = null;
  }

  private showHero(fileId: number): void {
    this.tab.previewLink(fileId).subscribe((link) => (this.heroPreview = link));
  }

  openDrafts(): void {
    this.nav.setTab('drafts');
  }

  // ------------------------------------------------------------------ structure: blocks inserted on this page

  addAt(after: string): void {
    this.editing = {
      after,
      section: { type: 'section', heading: '', _meta: { id: `id-s${shortId()}`, index: 0 } },
    };
  }

  edit(id: string): void {
    const block = this.blocks.find((b) => b._meta.id === id);
    if (block) this.editing = { after: null, section: block };
  }

  editDone(section: CmsPageSection): void {
    const e = this.editing;
    if (!e) return;
    this.blocks =
      e.after !== null
        ? addBlock(this.blocks, e.after, section)
        : replaceBlock(this.blocks, section);
    this.editing = null;
    this.refresh();
  }

  move(m: { id: string; dir: -1 | 1 }): void {
    const next = moveBlock(this.template, this.blocks, m.id, m.dir);
    if (!next) return;
    this.blocks = next;
    this.refresh();
  }

  remove(id: string): void {
    const heading = this.rows.find((r) => r.id === id)?.heading;
    const modal = this.modalService.generic({
      title: 'Delete block',
      copy: [
        `Delete ${heading ? `"${heading}"` : 'this block'} from this page? It goes when you save.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label: 'Delete', key: 'confirm', class: 'p-button-danger' },
      ],
    });
    modal?.onClose.pipe(take(1)).subscribe((key: string) => {
      if (key !== 'confirm') return;
      this.blocks = removeBlock(this.blocks, id);
      this.refresh();
    });
  }

  // ------------------------------------------------------------------ save, publish

  onSave(publishAction: SuburbPublishAction = null): void {
    const problem = this.editing
      ? 'Finish the block you are editing first (Done or Cancel).'
      : pageTabProblem(this.values, this.fromMap);
    if (problem) return this.problem(problem);
    if (!this.region) return this.problem('Pick the region this page belongs to.');
    this.saving = true;
    const published =
      publishAction === 'publish' ? true : publishAction === 'unpublish' ? false : null;
    this.tab
      .save(this.isNew, {
        original: this.record,
        values: this.values,
        blocks: blocksForSave(this.template, this.blocks),
        fromMap: this.fromMap,
        live: this.live,
        published,
      })
      .subscribe({
        next: (suburb) => this.ref.close({ saved: true, suburb, publishAction }),
        error: (err: unknown) => {
          this.saving = false;
          this.modalService.error(apiErrorBody(err));
        },
      });
  }

  unpublish(): void {
    const modal = this.modalService.generic({
      title: 'Take this page off the website?',
      copy: [
        'The page comes off the website preview now, and off picaluna.com when the preview is next published to production. Its content stays here, so you can publish it again.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label: 'Un-publish', key: 'unpublish', class: 'p-button-danger' },
      ],
    });
    modal?.onClose
      .pipe(take(1))
      .subscribe((key: string) => key === 'unpublish' && this.onSave('unpublish'));
  }

  /**
   * Developer: convert the page designed before the template into the template plus inserted blocks, once confirmed.
   * The tab closes after, so changes made here and not saved would be lost: the confirmation says so.
   */
  convert(): void {
    const blocks = blocksForSave(this.template, this.blocks);
    const unsaved =
      !!this.editing || hasUnsavedChanges({ original: this.record, values: this.values, blocks });
    const modal = this.modalService.generic({
      title: 'Convert this page?',
      copy: [
        "Its sections become this page's fields and blocks, and the page designed before the template is taken off this address (a Developer can revert it).",
        ...(unsaved
          ? [
              "You have changes in this tab that aren't saved: they will be lost. Cancel and Save first to keep them.",
            ]
          : []),
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        {
          label: unsaved ? 'Convert and lose my changes' : 'Convert',
          key: 'convert',
          class: unsaved ? 'p-button-danger' : '',
        },
      ],
    });
    modal?.onClose.pipe(take(1)).subscribe((key: string) => key === 'convert' && this.runConvert());
  }

  private runConvert(): void {
    this.saving = true;
    this.tab.convert(this.record).subscribe({
      next: () => this.ref.close({ saved: false, converted: true }),
      error: (err: unknown) => {
        this.saving = false;
        this.modalService.error(apiErrorBody(err));
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }

  private problem(text: string): void {
    this.form.markAllAsTouched();
    this.modalService.generic({
      title: "This page can't be saved yet",
      copy: [text],
      buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
    });
  }
}
